import {
  BarChart3,
  Briefcase,
  Building2,
  Calendar,
  FileText,
  Home,
  Layers,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Moon,
  PieChart,
  Settings,
  Share2,
  Sun,
  User,
  Users,
  Wallpaper,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import ConfirmDialog from "@/components/common/ConfirmDialog";
import { canAccessPermission, type Permission } from "@/constants/permissions";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { getTheme } from "@/redux/app/appSelector";
import { appActions } from "@/redux/app/appSlice";
import { logout } from "@/redux/auth/authSlice";

const BusinessLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const theme = useAppSelector(getTheme);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user role and business type for dynamic navigation
  const businessType =
    user?.role === "manager"
      ? "manager"
      : user?.role === "staff"
        ? "staff"
        : "admin";

  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // Apply theme to HTML element
  useEffect(() => {
    const htmlElement = document.querySelector("html");
    if (htmlElement) {
      htmlElement.setAttribute("data-theme", theme);
    }
  }, [theme]);

  // Theme toggle functionality
  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    dispatch(appActions.setTheme(newTheme));
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    (async () => {
      await import("@/services/authService").then(({ authService }) =>
        authService.logout(),
      );
      dispatch(logout());
      navigate("/auth/login");
      setShowLogoutConfirm(false);
    })();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Navigation items with permission requirements
  // Each item requires a specific permission to be visible
  // Admins automatically have access to ALL items
  const allNavigationItems = [
    {
      href: "/admin",
      icon: <Calendar className="h-5 w-5" />,
      name: "Calendar",
      requiredPermission: "dashboard_view" as Permission,
    },
    {
      href: "/admin/settings",
      icon: <Settings className="h-5 w-5" />,
      name: "Settings",
      requiredPermission: "settings_view" as Permission,
    },
    {
      href: "/admin/users",
      icon: <User className="h-5 w-5" />,
      name: "Users",
      requiredPermission: "user_management" as Permission,
    },
    {
      href: "/admin/reports",
      icon: <PieChart className="h-5 w-5" />,
      name: "Reports",
      requiredPermission: "reports_management" as Permission,
    },
    {
      href: "/admin/documents",
      icon: <FileText className="h-5 w-5" />,
      name: "Documents",
      requiredPermission: "documents_management" as Permission,
    },
    {
      href: "/admin/updates",
      icon: <MessageCircle className="h-5 w-5" />,
      name: "Updates",
      requiredPermission: "updates_management" as Permission,
    },
    {
      href: "/admin/careers",
      icon: <Briefcase className="h-5 w-5" />,
      name: "Careers",
      requiredPermission: "careers_management" as Permission,
    },
    {
      href: "/admin/sections",
      icon: <Layers className="h-5 w-5" />,
      name: "Sections",
      requiredPermission: "sections_management" as Permission,
    },
    {
      href: "/admin/community",
      icon: <Users className="h-5 w-5" />,
      name: "Community",
      requiredPermission: "community_management" as Permission,
    },
    {
      href: "/admin/hero-backgrounds",
      icon: <Wallpaper className="h-5 w-5" />,
      name: "Hero Backgrounds",
      requiredPermission: "hero_backgrounds_management" as Permission,
    },
    {
      href: "/admin/social-links",
      icon: <Share2 className="h-5 w-5" />,
      name: "Social Links",
      requiredPermission: "social_links" as Permission,
    },
    {
      href: "/admin/contact-info",
      icon: <MapPin className="h-5 w-5" />,
      name: "Contact Info",
      requiredPermission: "contact_info_management" as Permission,
    },
  ];

  /**
   * Filter navigation based on user role and permissions
   * - Admin users see ALL menu items
   * - Other users see only items they have permission for
   */
  const getFilteredNavigation = () => {
    return allNavigationItems.filter((item) =>
      canAccessPermission(user?.role, (user?.permissions as Permission[]) || [], item.requiredPermission),
    );
  };

  const navigation = getFilteredNavigation();

  function getManagementLabel(type: string): string {
    switch (type) {
      case "admin":
        return "Administrator management";
      case "manager":
        return "Partner management";
      case "staff":
        return "Employee management";
      default:
        return "Business operations";
    }
  }

  const businessTypeLabels = {
    admin: "Administrator",
    manager: "Partner",
    staff: "Employee",
  };

  const isCurrentPath = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-base-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-base-content opacity-75"></div>
        </div>
      )}

{/* Sidebar */}
<div
  className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-base-100 shadow-lg transition-transform duration-300 ease-in-out ${
    isMobile
      ? `transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`
      : ""
  }`}
>
  {/* Sidebar Header - fixed */}
  <div className="flex h-16 shrink-0 items-center justify-between bg-primary px-4 md:px-6">
    <Link className="flex items-center" to="/">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-base-100 md:h-10 md:w-10">
        <Building2 className="h-5 w-5 text-primary md:h-6 md:w-6" />
      </div>

      <span className="ml-2 text-xl font-bold text-primary-content md:text-2xl">
        Company
      </span>
    </Link>

    {isMobile && (
      <button
        className="text-white hover:text-gray-200"
        onClick={() => setSidebarOpen(false)}
      >
        <X className="h-6 w-6" />
      </button>
    )}
  </div>

  {/* Navigation - scrollable */}
  <nav className="min-h-0 flex-1 overflow-y-auto">
    <div className="mt-8 px-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-base-content/50">
        Business Portal
      </p>
    </div>

    {navigation.map((item) => (
      <Link
        className={`flex items-center px-4 py-3 text-sm font-medium transition-colors duration-150 md:px-6 md:py-4 md:text-base ${
          isCurrentPath(item.href)
            ? "border-r-3 border-primary bg-primary/10 text-primary"
            : "text-base-content hover:bg-base-200 hover:text-primary"
        }`}
        key={item.name}
        onClick={() => setSidebarOpen(false)}
        to={item.href}
      >
        <span
          className={`mr-3 md:mr-4 ${
            isCurrentPath(item.href)
              ? "text-primary"
              : "text-base-content/50"
          }`}
        >
          {item.icon}
        </span>

        {item.name}
      </Link>
    ))}
  </nav>

  {/* User info - fixed at bottom */}
  <div className="shrink-0 border-t border-base-300 bg-base-100 p-4 md:p-6">
    <div className="flex items-center">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/20 md:h-12 md:w-12">
        <User className="h-5 w-5 text-primary" />
      </div>

      <div className="ml-3 min-w-0 md:ml-4">
        <p className="truncate text-sm font-medium text-base-content md:text-base">
          {user?.firstName
            ? `${user.firstName} ${user.lastName}`.trim()
            : "Business Owner"}
        </p>

        <p className="text-xs text-base-content/60 md:text-sm">
          {businessTypeLabels[businessType]}
        </p>
      </div>
    </div>
  </div>
</div>

      {/* Main content */}
      <div className={isMobile ? "" : "md:pl-64"}>
        {/* Top navbar */}
        <div className="sticky top-0 z-30 border-b border-base-300 bg-base-100 shadow-sm">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 md:px-8">
            {/* Mobile menu button */}
            {isMobile && (
              <button
                className="rounded-md p-2 text-base-content/50 hover:bg-base-200 hover:text-base-content focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
            )}

            {/* Page title */}
            <div className="min-w-0 flex-1 md:ml-0">
              <h1 className="truncate text-lg font-semibold text-base-content md:text-xl">
                {navigation.find((item) => isCurrentPath(item.href))?.name ||
                  "Business Dashboard"}
              </h1>
            </div>

            {/* Right side - Theme toggle and Profile dropdown */}
            <div className="flex items-center space-x-3">
              {/* Theme Toggle */}
              <button
                className="flex items-center justify-center rounded-full p-2 text-base-content/60 hover:bg-base-200 hover:text-base-content focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                onClick={toggleTheme}
                title={
                  theme === "dark" ? "Switch to light mode" : "Switch to dark mode"
                }
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>

              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  className="flex cursor-pointer items-center rounded-full p-2 text-sm hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  onClick={() => {
                    setProfileDropdownOpen(!profileDropdownOpen);
                  }}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 z-10 mt-2 min-w-48 max-w-xs origin-top-right rounded-md bg-base-100 py-1 shadow-lg ring-1 ring-base-content ring-opacity-10 focus:outline-none">
                    <div className="border-b border-base-300 px-4 py-3">
                      <p className="text-sm font-medium text-base-content">
                        {user?.firstName
                          ? `${user.firstName} ${user.lastName}`.trim()
                          : "Business Owner"}
                      </p>
                      <p className="break-words text-sm text-base-content/60">
                        {user?.email}
                      </p>
                    </div>
                    <button
                        className="flex w-full items-center px-4 py-2 text-sm text-base-content hover:bg-base-200"
                      onClick={handleLogout}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmDialog
        cancelText="Cancel"
        confirmText="Sign out"
        message="Are you sure you want to sign out? You'll need to log in again to access your dashboard."
        onCancel={cancelLogout}
        onConfirm={confirmLogout}
        open={showLogoutConfirm}
        title="Confirm Logout"
        type="error"
      />
    </div>
  );
};

export default BusinessLayout;
